import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Icon,
  Badge,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from '@chakra-ui/react';
import { Shield, FileText, Brain, Activity, Users, Settings, ChevronDown } from 'lucide-react';

// Import panel components
import DashboardPanel from './governance/DashboardPanel';
import PolicyPanel from './governance/PolicyPanel';
import CompliancePanel from './governance/CompliancePanel';
import AuditPanel from './governance/AuditPanel';

interface GovernanceUIProps {
  onActivitySelect?: (activityType: string) => void;
}

const GovernanceUI: React.FC<GovernanceUIProps> = ({ onActivitySelect }) => {
  // State for active tab and user role
  const [activeTab, setActiveTab] = useState(0);
  const [activeRole, setActiveRole] = useState({
    name: 'Dr. Michael Thompson',
    role: 'Chief Governance Officer',
    team: 'Clinical Ethics',
    avatar: 'https://bit.ly/kent-c-dodds'
  });
  
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const methodistBlue = '#0066CC';

  // Multi-disciplinary team roles
  const teamRoles = [
    {
      name: 'Dr. Michael Thompson',
      role: 'Chief Governance Officer',
      team: 'Clinical Ethics',
      avatar: 'https://bit.ly/kent-c-dodds'
    },
    {
      name: 'Alex Rivera',
      role: 'Data Science Lead',
      team: 'Neural Lab',
      avatar: 'https://bit.ly/dan-abramov'
    },
    {
      name: 'Dr. James Wilson',
      role: 'Clinical Director',
      team: 'Medical Operations',
      avatar: 'https://bit.ly/sage-adebayo'
    },
    {
      name: 'Emma Thompson',
      role: 'Compliance Manager',
      team: 'Legal & Compliance',
      avatar: 'https://bit.ly/ryan-florence'
    }
  ];
  
  return (
    <Box height="100%" bg={useColorModeValue('gray.50', 'gray.900')} p={4} overflowY="auto">
      {/* Header */}
      <Flex mb={4} align="center" justify="space-between">
        <HStack spacing={4}>
          <HStack spacing={2}>
            <Icon as={Shield} color="methodist.blue" boxSize={6} />
            <Heading size="md" color={textColor}>Platform Governance</Heading>
          </HStack>
          <Badge colorScheme="purple" fontSize="sm" p={1}>Neural Integration</Badge>
          <Badge colorScheme="green" fontSize="sm" p={1}>Multi-Disciplinary</Badge>
        </HStack>
        <HStack spacing={4}>
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDown />} variant="ghost">
              <HStack>
                <Avatar size="sm" name={activeRole.name} src={activeRole.avatar} />
                <Box textAlign="left">
                  <Text fontSize="sm" fontWeight="medium">{activeRole.name}</Text>
                  <Text fontSize="xs" color="gray.500">{activeRole.team}</Text>
                </Box>
              </HStack>
            </MenuButton>
            <MenuList>
              {teamRoles.map((role, index) => (
                <MenuItem key={index} onClick={() => setActiveRole(role)}>
                  <HStack>
                    <Avatar size="sm" name={role.name} src={role.avatar} />
                    <Box>
                      <Text fontSize="sm">{role.name}</Text>
                      <Text fontSize="xs" color="gray.500">{role.team}</Text>
                    </Box>
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
      
      {/* Main content */}
      <Tabs variant="enclosed" colorScheme="blue" onChange={(index) => setActiveTab(index)} defaultIndex={0}>
        <TabList>
          <Tab><HStack><Icon as={Activity} boxSize={4} /><Text>Overview</Text></HStack></Tab>
          <Tab><HStack><Icon as={FileText} boxSize={4} /><Text>Policies</Text></HStack></Tab>
          <Tab><HStack><Icon as={Shield} boxSize={4} /><Text>Compliance</Text></HStack></Tab>
          <Tab><HStack><Icon as={Brain} boxSize={4} /><Text>Audits</Text></HStack></Tab>
        </TabList>
        
        <TabPanels>
          {/* Overview Panel */}
          <TabPanel p={0} pt={4}>
            <DashboardPanel />
          </TabPanel>
          
          {/* Policy Panel */}
          <TabPanel p={0} pt={4}>
            <PolicyPanel />
          </TabPanel>
          
          {/* Compliance Panel */}
          <TabPanel p={0} pt={4}>
            <CompliancePanel />
          </TabPanel>
          
          {/* Audit Panel */}
          <TabPanel p={0} pt={4}>
            <AuditPanel />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default GovernanceUI;
